import { Menu, styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { cloneElement, useState } from 'react';
import { Skeleton } from 'shell/components';
import Avatar from 'shell/components/Avatar';
import Box from 'shell/components/Box';
import Divider from 'shell/components/Divider';
import Icon from 'shell/components/Icon';
import MenuItem from 'shell/components/MenuItem';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        border: 'none',
        padding: 0,
        background: 'transparent',
        textAlign: 'left',
        cursor: 'pointer',
        '&:focus-visible': {
            borderRadius: '6px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '1rem',
    };
});

const CustomMenu = styled(Menu)(({ theme }) => {
    return {
        '& .MuiBackdrop-root': {
            background: 'transparent',
        },
        '& .MuiPaper-root': {
            boxShadow: theme.palette.ensightful.shadows[1],
        },
        '& .MuiList-root': {
            padding: theme.spacing(2),
        },
    };
});

const CustomText = styled(Text)(() => {
    return {
        fontSize: 'clamp(0.875rem, 1.2cqi, 1rem)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
});

const getIcon = (key) => {
    return {
        profile: <CustomIcon kind={'avatar'} />,
        'course-settings': <CustomIcon kind={'settings'} />,
        'switch-course': <CustomIcon kind={'resync'} />,
    }[key];
};

const renderUserItem = (user) => {
    return (
        <Stack key={'user'} direction={'row'} spacing={2} tabIndex={-1}>
            <Avatar size={'medium'} user={user} />
            <Stack justifyContent={'space-evenly'}>
                <Text variant={'p1'}>{`${user.display_name || user.first_name} ${user.last_name}`}</Text>
                <Text color={'light'} variant={'p3'}>
                    {user.email}
                </Text>
            </Stack>
        </Stack>
    );
};

const HeaderDropdown = ({ id, course, options, user, onSelect }) => {
    if (!id) throw new Error('Shell: Missing id in component Dropdown.');

    const { device } = useViewport();

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = (e, { key, value }) => {
        e.stopPropagation();
        setAnchorEl(null);
        if (key && value) onSelect({ key, value });
    };

    const open = Boolean(anchorEl);

    if (!user) return <Skeleton variant={'circular'} width={40} height={40} />;

    const RootComponent =
        device === 'tablet' ? (
            <CustomStack alignItems={'center'} component={'button'} direction={'row'} spacing={2}>
                <Avatar size={'medium'} user={user} />
                <CustomIcon kind={'dropdownArrowDown'} />
            </CustomStack>
        ) : (
            <CustomStack component={'button'} direction={'row'} spacing={2}>
                <Avatar size={'medium'} user={user} />
                <Stack sx={{ height: '2.5rem' }} justifyContent={'space-evenly'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomText color={'primary'} variant={'p1'}>{`${
                            user.display_name || user.first_name
                        } ${user.last_name.charAt(0)}.`}</CustomText>
                        <CustomIcon kind={'dropdownArrowDown'} />
                    </Stack>
                    {course?.name && (
                        <Text sx={{ maxWidth: '152px' }} noWrap ariant={'p3'}>
                            {course?.name}
                        </Text>
                    )}
                </Stack>
            </CustomStack>
        );

    return (
        <Box>
            {cloneElement(RootComponent, {
                id,
                'aria-controls': open ? `${id}-menu` : undefined,
                'aria-expanded': open ? true : undefined,
                'aria-haspopup': true,
                onClick: handleClick,
            })}
            <CustomMenu
                id={`${id}-menu`}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                MenuListProps={{
                    'aria-labelledby': id,
                }}
                open={open}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                onClose={handleClose}
            >
                {options.map(({ key, value }, index) => {
                    if (key === 'divider') return <Divider key={`${key}-${index}`} />;

                    if (key === 'user') {
                        return (
                            <MenuItem
                                key={`${key}-${index}`}
                                sx={{ height: 'auto' }}
                                onClick={(e) => handleClose(e, { key, value })}
                            >
                                {renderUserItem(user)}
                            </MenuItem>
                        );
                    }

                    return (
                        <MenuItem key={`${key}-${index}`} id={key} onClick={(e) => handleClose(e, { key, value })}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                {getIcon(key)}
                                {value}
                            </Stack>
                        </MenuItem>
                    );
                })}
            </CustomMenu>
        </Box>
    );
};

export default HeaderDropdown;
